import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./FormBuilder.css";
import {
  FiSave,
  FiEye,
  FiCheck,
  FiChevronLeft,
  FiChevronRight,
  FiPlus,
  FiTrash2,
  FiGrid,
  FiColumns,
  FiMenu,
  FiX,
  FiEdit,
  FiLink,
  FiList,
  FiPlusCircle,
  FiClipboard,
  FiEyeOff,
  FiPlay,
  FiXCircle,
  FiCopy,
  FiLayout,
  FiCalendar,
  FiClock,
  FiMove,
  FiDownload,
  FiGlobe,
} from "react-icons/fi";
import FormPreview from "./components/FormPreview";

// FormBuilder를 위한 번역 객체
const formBuilderTranslations = {
  ko: {
    // 기본 UI
    formBuilder: "폼 빌더",
    newForm: "새 폼 만들기",
    editForm: "폼 수정",
    save: "저장",
    saving: "저장 중...",
    publish: "공개",
    back: "돌아가기",
    downloadJSON: "JSON",
    tools: "도구",
    detailedEdit: "상세 편집",
    openPanel: "패널 열기",
    closePanel: "패널 닫기",

    // 필드 관련
    fieldElements: "필드 요소",
    text: "텍스트 필드",
    textarea: "텍스트 영역",
    number: "숫자 필드",
    select: "선택 필드",
    checkbox: "체크박스",
    radio: "라디오 버튼",
    date: "날짜 필드",
    datetime: "날짜 시간 필드",
    timediff: "시간 차이",
    time: "시간",
    file: "파일 업로드",
    calculated: "계산 필드",

    // 페이지/섹션/행 관련
    pageSettings: "페이지 설정",
    pageTitle: "페이지 제목",
    addPage: "페이지 추가",
    deletePage: "페이지 삭제",
    noPages: "페이지가 없습니다.",
    addPagePrompt: "왼쪽 도구에서 페이지를 추가하세요.",
    moveUp: "위로 이동",
    moveDown: "아래로 이동",
    addSection: "섹션 추가",
    noSections: "섹션이 없습니다. 섹션을 추가하세요.",
    section: "섹션",
    singleRow: "단일 행",
    twoColumnRow: "2열 행",
    addSingleRow: "단일 행 추가",
    addTwoColumnRow: "이중 행 추가",
    noRows: "행이 없습니다. 아래 버튼으로 행을 추가하세요.",
    dragFieldHere: "여기에 필드 요소를 드래그해주세요!",

    // 폼 속성
    formProperties: "폼 속성",
    formTitle: "폼 제목",
    formDescription: "폼 설명",

    // 버전 관련
    version: "버전",
    createNewForm: "새 폼 만들기",
    current: "현재",
    loading: "폼 로드 중...",

    // 기타
    unknownField: "알 수 없는 필드",

    // 하위 컴포넌트용 추가 번역 키
    // 섹션 에디터
    sectionSettings: "섹션 설정",
    sectionTitle: "섹션 제목",
    deleteSection: "섹션 삭제",
    selectSection: "섹션을 선택해주세요",
    confirmDeleteSection: "이 섹션을 삭제할까요?",
    enterSectionTitle: "섹션 제목 입력",

    // 행 에디터
    rowSettings: "행 설정",
    deleteRow: "행 삭제",
    selectRow: "행을 선택해주세요",
    confirmDeleteRow: "이 행을 삭제할까요?",

    // 필드 에디터
    fieldSettings: "필드 설정",
    fieldType: "필드 유형",
    fieldLabel: "필드 라벨",
    fieldPlaceholder: "필드 플레이스홀더",
    fieldRequired: "필수 항목",
    deleteField: "필드 삭제",
    selectFieldOrAddNew: "필드를 선택하거나 새 필드를 추가하세요",
    confirmDeleteField: "이 필드를 삭제할까요?",
    fieldId: "필드 ID",
    enterFieldId: "필드 ID 입력",
    enterFieldLabel: "필드 라벨 입력",
    enterPlaceholder: "플레이스홀더 텍스트 입력",
    fieldIdHelp: "고유한 필드 식별자를 입력하세요",

    // 조건부 표시
    conditionalDisplay: "조건부 표시",
    dependsOnField: "의존 필드",
    conditionalValue: "조건 값",
    selectDependencyField: "의존 필드 선택",
    conditionalDisplayHelp:
      "이 필드는 선택한 필드의 값이 조건을 만족할 때만 표시됩니다",

    // 비교 연산자
    equals: "같음 (==)",
    notEquals: "같지 않음 (!=)",
    contains: "포함함",
    notContains: "포함하지 않음",
    greaterThan: "보다 큼 (>)",
    lessThan: "보다 작음 (<)",
    greaterThanOrEqual: "이상 (>=)",
    lessThanOrEqual: "이하 (<=)",

    // 값
    value: "값",
  },
  en: {
    // 기본 UI
    formBuilder: "Form Builder",
    newForm: "Create New Form",
    editForm: "Edit Form",
    save: "Save",
    saving: "Saving...",
    publish: "Publish",
    back: "Back",
    downloadJSON: "JSON",
    tools: "Tools",
    detailedEdit: "Detailed Edit",
    openPanel: "Open Panel",
    closePanel: "Close Panel",

    // 필드 관련
    fieldElements: "Field Elements",
    text: "Text Field",
    textarea: "Text Area",
    number: "Number Field",
    select: "Select Field",
    checkbox: "Checkbox",
    radio: "Radio Button",
    date: "Date Field",
    datetime: "Date Time Field",
    timediff: "Time Difference",
    time: "Time",
    file: "File Upload",
    calculated: "Calculation Field",

    // 페이지/섹션/행 관련
    pageSettings: "Page Settings",
    pageTitle: "Page Title",
    addPage: "Add Page",
    deletePage: "Delete Page",
    noPages: "No pages available.",
    addPagePrompt: "Add a page from the tools panel.",
    moveUp: "Move Up",
    moveDown: "Move Down",
    addSection: "Add Section",
    noSections: "No sections. Please add a section.",
    section: "Section",
    singleRow: "Single Row",
    twoColumnRow: "Two Column Row",
    addSingleRow: "Add Single Row",
    addTwoColumnRow: "Add Two Column Row",
    noRows: "No rows. Use the buttons below to add a row.",
    dragFieldHere: "Drag field elements here!",

    // 폼 속성
    formProperties: "Form Properties",
    formTitle: "Form Title",
    formDescription: "Form Description",

    // 버전 관련
    version: "Version",
    createNewForm: "Create New Form",
    current: "Current",
    loading: "Loading form...",

    // 기타
    unknownField: "Unknown Field",

    // 하위 컴포넌트용 추가 번역 키
    // 섹션 에디터
    sectionSettings: "Section Settings",
    sectionTitle: "Section Title",
    deleteSection: "Delete Section",
    selectSection: "Select a section",
    confirmDeleteSection: "Are you sure you want to delete this section?",
    enterSectionTitle: "Enter section title",

    // 행 에디터
    rowSettings: "Row Settings",
    deleteRow: "Delete Row",
    selectRow: "Select a row",
    confirmDeleteRow: "Are you sure you want to delete this row?",

    // 필드 에디터
    fieldSettings: "Field Settings",
    fieldType: "Field Type",
    fieldLabel: "Field Label",
    fieldPlaceholder: "Field Placeholder",
    fieldRequired: "Required",
    deleteField: "Delete Field",
    selectFieldOrAddNew: "Select a field or add a new one",
    confirmDeleteField: "Are you sure you want to delete this field?",
    fieldId: "Field ID",
    enterFieldId: "Enter field ID",
    enterFieldLabel: "Enter field label",
    enterPlaceholder: "Enter placeholder text",
    fieldIdHelp: "Enter a unique identifier for this field",

    // 조건부 표시
    conditionalDisplay: "Conditional Display",
    dependsOnField: "Depends On Field",
    conditionalValue: "Conditional Value",
    selectDependencyField: "Select dependency field",
    conditionalDisplayHelp:
      "This field will only be displayed when the selected field meets the condition",

    // 비교 연산자
    equals: "Equals (==)",
    notEquals: "Not Equals (!=)",
    contains: "Contains",
    notContains: "Not Contains",
    greaterThan: "Greater Than (>)",
    lessThan: "Less Than (<)",
    greaterThanOrEqual: "Greater Than or Equal (>=)",
    lessThanOrEqual: "Less Than or Equal (<=)",

    // 값
    value: "Value",
  },
  // 나머지 언어도 동일한 구조로 추가 가능
};

// 기존 컴포넌트
import FieldEditor from "./components/FieldEditor";
import PageEditor from "./components/PageEditor";
import SectionEditor from "./components/SectionEditor";
import RowEditor from "./components/RowEditor";

// 유틸리티 함수 임포트
import * as dragDropUtils from "./utils/dragDropUtils";
import * as formStateUtils from "./utils/formStateUtils";
import { createNewField } from "./utils/formUtils";
import { checkConditionValue } from "./utils/conditionUtils";

// 새로운 변환 유틸리티 함수
import {
  convertToApiFormat,
  convertFromApiFormat,
} from "./utils/formatConverters";
import { saveForm } from "./handlers/formHandlers";
import {
  handleDragStart as dndDragStart,
  handleDrop as dndHandleDrop,
} from "./handlers/dragDropHandlers";
import { FaArrowUp, FaArrowDown } from "react-icons/fa";

// 실제 필드 컴포넌트 - NewCase에서 사용
import {
  DynamicField,
  ConditionalField,
  DateWithAgeField,
} from "../fields/fields";
import CheckboxField from "../fields/components/CheckboxField";

// NewCase 레이아웃 컴포넌트 재사용
import { SingleColumnLayout, TwoColumnLayout } from "../fields/fields";

// 필드 타입 상수
export const FIELD_TYPES = {
  TEXT: "text",
  TEXTAREA: "textarea",
  NUMBER: "number",
  SELECT: "select",
  CHECKBOX: "checkbox",
  RADIO: "radio",
  DATE: "date",
  DATETIME: "datetime",
  TIMEDIFF: "timediff",
};

// 행 레이아웃 상수
const ROW_LAYOUTS = {
  FULL: "full",
  TWO_COLUMN: "two-column",
};

// 필드 타입별 기본 속성
const DEFAULT_FIELD_PROPERTIES = {
  [FIELD_TYPES.TEXT]: {
    label: "텍스트 필드",
    placeholder: "텍스트를 입력하세요",
    required: false,
    maxLength: 100,
  },
  [FIELD_TYPES.TEXTAREA]: {
    label: "텍스트 영역",
    placeholder: "여러 줄의 텍스트를 입력하세요",
    required: false,
    rows: 4,
  },
  [FIELD_TYPES.NUMBER]: {
    label: "숫자 필드",
    placeholder: "숫자를 입력하세요",
    required: false,
    min: 0,
    max: 100,
    step: 1,
  },
  [FIELD_TYPES.SELECT]: {
    label: "선택 필드",
    placeholder: "옵션을 선택하세요",
    required: false,
    options: [
      { value: "option1", label: "옵션 1" },
      { value: "option2", label: "옵션 2" },
      { value: "option3", label: "옵션 3" },
    ],
  },
  [FIELD_TYPES.CHECKBOX]: {
    label: "체크박스",
    required: false,
    options: [
      { value: "option1", label: "옵션 1" },
      { value: "option2", label: "옵션 2" },
      { value: "option3", label: "옵션 3" },
    ],
  },
  [FIELD_TYPES.RADIO]: {
    label: "라디오 버튼",
    required: false,
    options: [
      { value: "option1", label: "옵션 1" },
      { value: "option2", label: "옵션 2" },
      { value: "option3", label: "옵션 3" },
    ],
  },
  [FIELD_TYPES.DATE]: {
    label: "날짜 필드",
    required: false,
    min: "",
    max: "",
  },
  [FIELD_TYPES.DATETIME]: {
    label: "날짜 시간 필드",
    required: false,
    min: "",
    max: "",
  },
  [FIELD_TYPES.TIMEDIFF]: {
    label: "시간 차이",
    required: false,
    startField: "",
    endField: "",
    displayUnit: "minutes",
    useAbsoluteValue: false,
  },
};

import "../newcase/styles/variables.css";
import "../newcase/styles/form.css";
import "../newcase/styles/components.css";
import "../newcase/styles/typography.css";

// 맨 위 import
import {
  addRow,
  deleteRow as removeRow,
  updateRowLayout,
  moveRow as moveRowHandler,
  normalizeRowOrders,
  findSectionIdForRow as findSectionIdForRowHandler,
} from "./handlers/rowHandlers";

import {
  addField,
  deleteField,
  updateFieldProperties,
  moveField,
  updateFieldDependency,
  removeFieldDependency,
  renameFieldId,
} from "./handlers/fieldHandlers";

import {
  addPage,
  deletePage as removePage,
  updatePageTitle,
  movePage as movePageHandler,
} from "./handlers/pageHandlers";

import {
  addSection as addSectionHandler,
  deleteSection as removeSection,
  updateSectionTitle,
  moveSection as moveSectionHandler,
  findPageIdForSection as findPageIdForSectionHandler,
} from "./handlers/sectionHandlers";

import { formAPI } from "../../../services/api";
import { useAuth } from "../../../contexts/AuthContext";

/**
 * 폼 빌더 컴포넌트
 * 폼 구조를 생성하고 편집하는 기능을 제공합니다.
 */
const FormBuilder = () => {
  const { formId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  // 다국어 지원을 위한 상태 추가
  const [currentLanguage, setCurrentLanguage] = useState(
    localStorage.getItem("preferredLanguage") || "ko"
  );

  // 번역 함수
  const t = (key) => {
    if (formBuilderTranslations[currentLanguage]?.[key]) {
      return formBuilderTranslations[currentLanguage][key];
    }
    // 번역이 없으면 한국어 반환, 한국어도 없으면 키 자체를 반환
    return formBuilderTranslations.ko[key] || key;
  };

  // 언어 변경 핸들러
  const handleLanguageChange = (lang) => {
    setCurrentLanguage(lang);
    localStorage.setItem("preferredLanguage", lang);
  };

  // 폼 버전 관련 상태 추가
  const [formVersions, setFormVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);

  // 폼 구조 상태
  const [formStructure, setFormStructure] = useState(
    formStateUtils.createEmptyFormStructure()
  );

  // 선택된 요소 상태
  const [selectedElement, setSelectedElement] = useState({
    type: "form",
    id: null,
  });

  // 패널 접기/펼치기 상태
  const [toolsPanelCollapsed, setToolsPanelCollapsed] = useState(false);
  const [propertiesPanelCollapsed, setPropertiesPanelCollapsed] =
    useState(false);
  const [panelWidth, setPanelWidth] = useState(450);
  const [toolsPanelWidth, setToolsPanelWidth] = useState(260);
  const resizingRef = useRef(null);
  const leftResizingRef = useRef(null);

  // 폼 로드 및 저장 상태
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(!!formId);

  // 폼 버전 로드 함수
  const loadFormVersions = useCallback(async () => {
    try {
      setIsLoadingVersions(true);

      // 세션 스토리지에서 현재 프로젝트 정보 읽기
      const currentProject = JSON.parse(
        sessionStorage.getItem("currentProject") || "{}"
      );
      const projectId = currentProject.id;

      if (!projectId) {
        console.error("프로젝트 ID가 없습니다.");
        return;
      }

      // API 호출하여 폼 버전 목록 가져오기
      const response = await formAPI.getFormVersions(projectId);

      if (response && response.versions) {
        setFormVersions(response.versions);

        // 활성화된 버전이 있다면 선택
        const activeVersion = response.versions.find((v) => v.is_active);
        if (activeVersion) {
          setSelectedVersion(activeVersion);

          // form_structure JSON 필드 처리 (이미 파싱되었거나 문자열일 수 있음)
          const data =
            typeof activeVersion.form_structure === "string"
              ? JSON.parse(activeVersion.form_structure)
              : activeVersion.form_structure;

          setFormStructure(data);
        }
      }
    } catch (error) {
      console.error("폼 버전 로드 중 오류:", error);
    } finally {
      setIsLoadingVersions(false);
    }
  }, []);

  // 컴포넌트 마운트 시 폼 버전 로드
  useEffect(() => {
    loadFormVersions();
  }, [loadFormVersions]);

  // 버전 변경 핸들러
  const handleVersionChange = async (versionId) => {
    try {
      const version = formVersions.find((v) => v.id === versionId);
      if (version) {
        setSelectedVersion(version);
        setFormStructure(version.form_structure);
      }
    } catch (error) {
      console.error("버전 변경 중 오류:", error);
    }
  };

  // 폼 로드 함수
  useEffect(() => {
    if (formId) {
      setIsLoading(true);

      // TODO: API 호출로 변경
      const loadForm = async () => {
        try {
          // 폼 데이터 가져오는 API 호출 (임시 목업)
          const mockFormData = {
            template_id: formId,
            group_id: 1,
            form_name: "기존 폼",
            form_code: "FORM",
            form_version: "1.0",
            is_active: 1,
            pages: [
              {
                page_number: 1,
                page_title: "페이지 1",
                sections: [
                  {
                    section_id: "section_1",
                    section_title: "섹션 1",
                    fields: [
                      {
                        field_id: "field_1",
                        type: FIELD_TYPES.TEXT,
                        label: "이름",
                        placeholder: "이름을 입력하세요",
                        required: true,
                        layout: "half",
                      },
                    ],
                  },
                ],
              },
            ],
          };

          // API 형식에서 FormBuilder 형식으로 변환
          const formBuilderData = convertFromApiFormat(mockFormData);
          setFormStructure(formBuilderData);
          setIsEditMode(true);
        } catch (error) {
          console.error("폼 로드 오류:", error);
          // 오류 처리
        } finally {
          setIsLoading(false);
        }
      };

      loadForm();
    }
  }, [formId]);

  // 폼 저장 함수
  const handleSaveForm = async () => {
    setIsSaving(true);

    try {
      // 세션 스토리지에서 현재 프로젝트 정보 읽기
      const currentProject = JSON.parse(
        sessionStorage.getItem("currentProject") || "{}"
      );
      const projectId = currentProject.id;

      if (!projectId) {
        throw new Error("프로젝트 정보를 찾을 수 없습니다.");
      }

      // 폼 기본 정보 준비
      const formTitle = formStructure.title || "새 폼";
      const templateCode = generateTemplateCode(formTitle); // 폼 제목으로 코드 생성
      const totalPages = formStructure.pages ? formStructure.pages.length : 1;
      const description = formStructure.description || "";

      // 버전 관련 정보 (수정 시)
      const isUpdate = selectedVersion && selectedVersion.id;
      const changeNotes = window.prompt(
        "변경 내용을 입력하세요 (선택사항)",
        isUpdate ? "폼 업데이트" : "최초 버전"
      );

      // 활성화 여부 확인 (선택사항)
      const makeActive = window.confirm(
        "이 버전을 활성 버전으로 설정하시겠습니까?"
      );

      // API 요청 데이터 구성
      const saveData = {
        project_id: projectId,
        template_code: templateCode,
        name: formTitle,
        description: description,
        total_pages: totalPages,
        form_structure: formStructure,
        change_notes: changeNotes || "",
        make_active: makeActive,
      };

      // API 호출
      const response = await formAPI.saveForm(saveData);

      if (response && response.success) {
        // 성공 메시지 표시
        alert(
          `폼이 성공적으로 저장되었습니다. (버전 ${response.data.version_number})`
        );

        // 상태 업데이트
        if (isUpdate) {
          // 버전 목록 갱신
          loadFormVersions();
        } else {
          // 새 폼인 경우 ID 업데이트
          setSelectedVersion({
            id: response.data.version_id,
            template_id: response.data.template_id,
            version_number: response.data.version_number,
          });
          setIsEditMode(true);
        }
      } else {
        throw new Error(response?.message || "저장 실패");
      }
    } catch (error) {
      console.error("폼 저장 오류:", error);

      // 오류 메시지 표시
      const errData = error.response?.data;
      if (errData) {
        alert(
          `[${errData.step || "unknown"} 단계 오류]\n` +
            `${errData.message || error.message}\n` +
            (errData.error ? `상세: ${errData.error}` : "")
        );
      } else {
        alert(`폼 저장 중 오류가 발생했습니다: ${error.message}`);
      }
    } finally {
      setIsSaving(false);
    }
  };

  // 폼 공개 함수
  const handlePublishForm = async () => {
    // TODO: 폼 공개 API 호출
    alert("폼이 성공적으로 공개되었습니다.");
  };

  // 드래그 시작 핸들러 (위임)
  const handleDragStart = dndDragStart;

  // 드래그 앤 드롭 이벤트 핸들러 (위임)
  const handleDrop = (event, dropTargetType, dropTargetId) => {
    event.preventDefault();
    event.stopPropagation();

    const dataStr = event.dataTransfer.getData("application/json");
    if (!dataStr) return;
    const dragData = JSON.parse(dataStr);

    try {
      const { updatedStructure, selectedElement: sel } = dndHandleDrop(
        formStructure,
        dragData,
        dropTargetType,
        dropTargetId
      );
      if (updatedStructure) setFormStructure(updatedStructure);
      if (sel) setSelectedElement(sel);
    } catch (err) {
      console.error("드래그 앤 드롭 처리 중 오류:", err);
    }

    event.currentTarget.classList.remove("dragover");
  };

  // 페이지 추가 핸들러
  const handleAddPage = () => {
    const updatedFormStructure = addPage(formStructure);
    setFormStructure(updatedFormStructure);

    // 새로 추가된 페이지 선택
    const newPageId =
      updatedFormStructure.pages[updatedFormStructure.pages.length - 1].id;
    setSelectedElement({
      type: "page",
      id: newPageId,
    });
  };

  // 페이지 삭제 핸들러
  const handleDeletePage = (pageId) => {
    const updatedFormStructure = removePage(formStructure, pageId);
    setFormStructure(updatedFormStructure);

    // 폼 선택으로 초기화
    setSelectedElement({
      type: "form",
      id: null,
    });
  };

  // 페이지 업데이트 핸들러
  const handleUpdatePage = (pageId, updates) => {
    const { title } = updates;
    const updatedFormStructure = updatePageTitle(formStructure, pageId, title);
    setFormStructure(updatedFormStructure);
  };

  // 섹션 추가 핸들러
  const handleAddSection = (pageId) => {
    const updatedFormStructure = addSectionHandler(formStructure, pageId);
    setFormStructure(updatedFormStructure);

    // 새로 추가된 섹션 찾기
    const updatedPage = updatedFormStructure.pages.find(
      (page) => page.id === pageId
    );
    if (updatedPage) {
      const newSectionId =
        updatedPage.sections[updatedPage.sections.length - 1].id;

      // 새로 추가된 섹션 선택
      setSelectedElement({
        type: "section",
        id: newSectionId,
      });
    }
  };

  // 섹션 삭제 핸들러
  const handleDeleteSection = (sectionId) => {
    const updatedFormStructure = removeSection(formStructure, sectionId);
    setFormStructure(updatedFormStructure);

    // 페이지 선택으로 초기화
    const pageId = findPageIdForSectionHandler(formStructure, sectionId);
    if (pageId) {
      setSelectedElement({
        type: "page",
        id: pageId,
      });
    } else {
      setSelectedElement({
        type: "form",
        id: null,
      });
    }
  };

  // 섹션 업데이트 핸들러
  const handleUpdateSection = (sectionId, updates) => {
    // formStructure를 직접 수정
    const updatedFormStructure = {
      ...formStructure,
      pages: formStructure.pages.map((page) => ({
        ...page,
        sections: page.sections.map((section) => {
          if (section.id === sectionId) {
            return {
              ...section,
              ...updates,
            };
          }
          return section;
        }),
      })),
    };

    setFormStructure(updatedFormStructure);
  };

  // 행 추가
  const handleAddRow = (sectionId, layout = "full") => {
    const updated = addRow(formStructure, sectionId, layout);
    setFormStructure(updated);
    // 새 행 선택 로직 ...
  };

  // 행 삭제
  const handleDeleteRow = (rowId) => {
    const updated = removeRow(formStructure, rowId);
    setFormStructure(updated);
    // 선택 로직 ...
  };

  // 행 업데이트
  const handleUpdateRow = (rowId, updates) => {
    const updated = updateRowLayout(formStructure, rowId, updates.layout);
    setFormStructure(updated);
  };

  // ----- 행 이동 -----
  const handleMoveRowUp = (rowId) => {
    const sectionId = findSectionIdForRowHandler(formStructure, rowId);
    if (!sectionId) return;
    const section = formStructure.pages
      .flatMap((p) => p.sections)
      .find((s) => s.id === sectionId);
    if (!section) return;
    const idx = section.rows.findIndex((r) => r.id === rowId);
    if (idx <= 0) return; // 이미 맨 위
    const updated = normalizeRowOrders(
      moveRowHandler(formStructure, rowId, sectionId, idx - 1)
    );
    setFormStructure(updated);
  };

  const handleMoveRowDown = (rowId) => {
    const sectionId = findSectionIdForRowHandler(formStructure, rowId);
    if (!sectionId) return;
    const section = formStructure.pages
      .flatMap((p) => p.sections)
      .find((s) => s.id === sectionId);
    if (!section) return;
    const idx = section.rows.findIndex((r) => r.id === rowId);
    if (idx === -1 || idx >= section.rows.length - 1) return; // 맨 아래
    const updated = normalizeRowOrders(
      moveRowHandler(formStructure, rowId, sectionId, idx + 1)
    );
    setFormStructure(updated);
  };

  // ----- 섹션 이동 -----
  const handleMoveSectionUp = (sectionId) => {
    const pageId = findPageIdForSectionHandler(formStructure, sectionId);
    if (!pageId) return;
    const page = formStructure.pages.find((p) => p.id === pageId);
    const idx = page.sections.findIndex((s) => s.id === sectionId);
    if (idx <= 0) return;
    const updated = moveSectionHandler(
      formStructure,
      sectionId,
      pageId,
      idx - 1
    );
    setFormStructure(updated);
  };

  const handleMoveSectionDown = (sectionId) => {
    const pageId = findPageIdForSectionHandler(formStructure, sectionId);
    if (!pageId) return;
    const page = formStructure.pages.find((p) => p.id === pageId);
    const idx = page.sections.findIndex((s) => s.id === sectionId);
    if (idx === -1 || idx >= page.sections.length - 1) return;
    const updated = moveSectionHandler(
      formStructure,
      sectionId,
      pageId,
      idx + 1
    );
    setFormStructure(updated);
  };

  // ----- 페이지 이동 -----
  const handleMovePageUp = (pageId) => {
    const idx = formStructure.pages.findIndex((p) => p.id === pageId);
    if (idx <= 0) return;
    const updated = movePageHandler(formStructure, pageId, idx - 1);
    setFormStructure(updated);
  };

  const handleMovePageDown = (pageId) => {
    const idx = formStructure.pages.findIndex((p) => p.id === pageId);
    if (idx === -1 || idx >= formStructure.pages.length - 1) return;
    const updated = movePageHandler(formStructure, pageId, idx + 1);
    setFormStructure(updated);
  };

  // 필드 삭제 핸들러
  const handleDeleteField = (fieldId) => {
    const updatedFormStructure = formStateUtils.deleteField(
      formStructure,
      fieldId
    );
    setFormStructure(updatedFormStructure);

    // 행 선택으로 초기화
    const rowId = findRowIdForField(formStructure, fieldId);
    if (rowId) {
      setSelectedElement({
        type: "row",
        id: rowId,
      });
    } else {
      setSelectedElement({
        type: "form",
        id: null,
      });
    }
  };

  // 행에 필드 추가 핸들러
  const handleAddField = (rowId) => {
    // 필드 유형 선택을 위한 대화상자 표시
    const fieldType = prompt(
      "추가할 필드 유형을 선택하세요: text, textarea, number, select, checkbox, radio, date, datetime",
      "text"
    );

    if (!fieldType) return; // 사용자가 취소한 경우

    // 선택한 유형으로 새 필드 생성
    const newField = formStateUtils.createNewField(fieldType);

    // 필드를 행에 추가
    const updatedFormStructure = formStateUtils.addFieldToRow(
      formStructure,
      rowId,
      newField
    );

    if (updatedFormStructure) {
      setFormStructure(updatedFormStructure);

      // 새로 추가된 필드 선택
      setSelectedElement({
        type: "field",
        id: newField.id,
      });
    }
  };

  // 필드 업데이트 핸들러
  const handleUpdateField = (fieldId, updates) => {
    const updatedFormStructure = formStateUtils.updateFieldProperties(
      formStructure,
      fieldId,
      updates
    );
    setFormStructure(updatedFormStructure);
  };

  // 필드 의존성 업데이트 핸들러
  const handleUpdateFieldDependency = (fieldId, dependency) => {
    const updatedFormStructure = formStateUtils.updateFieldDependency(
      formStructure,
      fieldId,
      dependency
    );
    setFormStructure(updatedFormStructure);
  };

  // 필드 의존성 제거 핸들러
  const handleRemoveFieldDependency = (fieldId) => {
    const updatedFormStructure = formStateUtils.removeFieldDependency(
      formStructure,
      fieldId
    );
    setFormStructure(updatedFormStructure);
  };

  // 요소 ID 찾기 유틸리티 함수들
  const findPageIdForSection = (formStructure, sectionId) => {
    for (const page of formStructure.pages) {
      if (page.sections.some((section) => section.id === sectionId)) {
        return page.id;
      }
    }
    return null;
  };

  const findRowIdForField = (formStructure, fieldId) => {
    for (const page of formStructure.pages) {
      for (const section of page.sections) {
        for (const row of section.rows) {
          if (row.fields.some((field) => field.id === fieldId)) {
            return row.id;
          }
        }
      }
    }
    return null;
  };

  // 요소 클릭 핸들러
  const handleElementClick = (type, id) => {
    setSelectedElement({ type, id });
  };

  // 드래그 오버 핸들러
  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add("dragover");
  };

  // 드래그 리브 핸들러
  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove("dragover");
  };

  // 필드 의존성 상태 확인 함수 (안전하게 문자열 반환)
  const getDependencyState = (dependency) => {
    if (!dependency) return null;
    if (typeof dependency === "string") return "조건부 필드";
    if (typeof dependency === "object") return "조건부 필드";
    return "조건부 필드";
  };

  // helper function to safely get field text representation
  const getFieldText = (field) => {
    if (!field) return "";
    if (typeof field === "string") return field;
    if (
      typeof field === "object" &&
      field.label &&
      typeof field.label === "string"
    )
      return field.label;
    return String(field);
  };

  // helper to render preview field using actual components
  const renderFieldPreview = (field) => {
    const dummyOnChange = () => {};

    // 실제 입력 컴포넌트 생성
    let inputComponent = null;
    if (field.type === FIELD_TYPES.CHECKBOX) {
      // 체크박스 옵션들을 개별 렌더링 (읽기 전용)
      const opts =
        field.options && field.options.length
          ? field.options
          : [{ label: field.label, value: "yes" }];
      inputComponent = (
        <div className="checkbox-preview-group">
          {opts.map((opt, idx) => (
            <CheckboxField
              key={idx}
              checked={false}
              onChange={() => {}}
              label={opt.label || opt.value || `옵션${idx + 1}`}
              required={field.required}
            />
          ))}
        </div>
      );
    } else if (field.type === FIELD_TYPES.DATE && field.showAge) {
      inputComponent = (
        <DateWithAgeField
          field={field}
          value={""}
          onChange={dummyOnChange}
          disabled
        />
      );
    } else if (field.dependsOn) {
      inputComponent = (
        <ConditionalField
          field={field}
          value={""}
          dependsOn={null}
          formData={{}}
          onChange={dummyOnChange}
          disabled
        />
      );
    } else if (field.type === FIELD_TYPES.TIMEDIFF) {
      inputComponent = (
        <div className="timediff-preview">
          <div className="timediff-value">
            <span className="calculated-value">0</span>
            <span className="unit-label">{field.displayUnit || "분"}</span>
          </div>
        </div>
      );
    } else {
      inputComponent = (
        <DynamicField
          field={field}
          value={""}
          onChange={dummyOnChange}
          disabled
        />
      );
    }

    return (
      <div className="builder-field" style={{ width: "100%" }}>
        {field.label && (
          <label
            className="field-label"
            style={{ display: "block", fontWeight: 600, marginBottom: 4 }}
          >
            {field.label}
          </label>
        )}
        {inputComponent}
      </div>
    );
  };

  // 폼 요소 렌더링 함수
  const renderFormElement = () => {
    const { type, id } = selectedElement;

    // 현재 선택된 요소 가져오기
    const selectedElementData = formStateUtils.findElementInFormStructure(
      formStructure,
      type,
      id
    );

    switch (type) {
      case "form":
        return (
          <div className="form-properties">
            <h3>{t("formProperties")}</h3>
            <div className="property-group">
              <label>{t("formTitle")}</label>
              <input
                type="text"
                value={formStructure.title || ""}
                onChange={(e) =>
                  setFormStructure({
                    ...formStructure,
                    title: e.target.value,
                  })
                }
              />
            </div>
            <div className="property-group">
              <label>{t("formDescription")}</label>
              <textarea
                value={formStructure.description || ""}
                onChange={(e) =>
                  setFormStructure({
                    ...formStructure,
                    description: e.target.value,
                  })
                }
              />
            </div>
          </div>
        );

      case "page":
        if (!selectedElementData) return null;

        return (
          <div className="page-properties">
            <h3>{t("pageSettings")}</h3>

            <div className="property-group">
              <label>{t("pageTitle")}</label>
              <input
                type="text"
                value={selectedElementData.title || ""}
                onChange={(e) =>
                  handleUpdatePage(id, { title: e.target.value })
                }
              />
            </div>

            <div
              className="property-group"
              style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}
            >
              <button
                className="btn btn-outline"
                onClick={() => handleMovePageUp(id)}
              >
                <FaArrowUp /> {t("moveUp")}
              </button>
              <button
                className="btn btn-outline"
                onClick={() => handleMovePageDown(id)}
              >
                <FaArrowDown /> {t("moveDown")}
              </button>
              <button
                className="btn btn-danger"
                onClick={() => handleDeletePage(id)}
              >
                {t("deletePage")}
              </button>
            </div>
          </div>
        );

      case "section":
        if (!selectedElementData) return null;

        return (
          <SectionEditor
            selectedSection={selectedElementData}
            onUpdateSection={(updates) => handleUpdateSection(id, updates)}
            onDeleteSection={() => handleDeleteSection(id)}
            availableFields={formStateUtils.getAllFieldIdsAsStrings(
              formStructure
            )}
            onMoveSectionUp={handleMoveSectionUp}
            onMoveSectionDown={handleMoveSectionDown}
            t={t}
          />
        );

      case "row":
        if (!selectedElementData) return null;

        return (
          <RowEditor
            selectedRow={selectedElementData}
            onUpdateRow={(updates) => handleUpdateRow(id, updates)}
            onDeleteRow={() => handleDeleteRow(id)}
            onMoveRowUp={handleMoveRowUp}
            onMoveRowDown={handleMoveRowDown}
            t={t}
          />
        );

      case "field":
        if (!selectedElementData) return null;

        // 안전하게 필드 정보를 가져옴 - 전체 객체 대신 문자열 배열로 변환
        const availableFields =
          formStateUtils.getAllFieldIdsAsStrings(formStructure);

        return (
          <FieldEditor
            selectedField={selectedElementData}
            availableFields={availableFields}
            onUpdateField={(updates) => handleUpdateField(id, updates)}
            onUpdateDependency={(dependency) =>
              handleUpdateFieldDependency(id, dependency)
            }
            onRemoveDependency={() => handleRemoveFieldDependency(id)}
            onDeleteField={() => handleDeleteField(id)}
            onRenameFieldId={handleRenameFieldId}
            t={t}
          />
        );

      default:
        return null;
    }
  };

  // 패널 토글 핸들러
  const toggleToolsPanel = () => {
    setToolsPanelCollapsed(!toolsPanelCollapsed);
  };

  const togglePropertiesPanel = () => {
    setPropertiesPanelCollapsed(!propertiesPanelCollapsed);
  };

  // 사이드바 리사이즈 핸들러
  const handleResizeMouseDown = (e) => {
    resizingRef.current = { startX: e.clientX, startWidth: panelWidth };
    window.addEventListener("mousemove", handleResizing);
    window.addEventListener("mouseup", stopResizing);
  };

  const handleResizing = (e) => {
    if (!resizingRef.current) return;
    const { startX, startWidth } = resizingRef.current;
    const delta = startX - e.clientX; // 왼쪽으로 드래그 → 확대
    let newWidth = startWidth + delta;
    if (newWidth < 350) newWidth = 350;
    if (newWidth > 600) newWidth = 600;
    setPanelWidth(newWidth);
  };

  const stopResizing = () => {
    window.removeEventListener("mousemove", handleResizing);
    window.removeEventListener("mouseup", stopResizing);
    resizingRef.current = null;
  };

  // ----- 왼쪽 리사이저(도구 패널 폭) -----
  const handleLeftResizeMouseDown = (e) => {
    leftResizingRef.current = {
      startX: e.clientX,
      startWidth: toolsPanelWidth,
    };
    window.addEventListener("mousemove", handleLeftResizing);
    window.addEventListener("mouseup", stopLeftResizing);
  };

  const handleLeftResizing = (e) => {
    if (!leftResizingRef.current) return;
    const { startX, startWidth } = leftResizingRef.current;
    const delta = e.clientX - startX; // 오른쪽으로 드래그 → 확대
    let newWidth = startWidth + delta;
    if (newWidth < 200) newWidth = 200;
    if (newWidth > 400) newWidth = 400;
    setToolsPanelWidth(newWidth);
  };

  const stopLeftResizing = () => {
    window.removeEventListener("mousemove", handleLeftResizing);
    window.removeEventListener("mouseup", stopLeftResizing);
    leftResizingRef.current = null;
  };

  // 필드 ID 변경 핸들러
  const handleRenameFieldId = (oldId, newId) => {
    if (!newId || oldId === newId) return;
    const updated = formStateUtils.renameFieldId(formStructure, oldId, newId);
    setFormStructure(updated);
    setSelectedElement({ type: "field", id: newId });
  };

  // DEFAULT_FIELD_PROPERTIES 안전한 접근 헬퍼 함수
  const getFieldLabel = (fieldType) => {
    if (!fieldType) return t("unknownField");
    return t(fieldType);
  };

  const handleDownloadJSON = () => {
    const data = convertToApiFormat(formStructure);
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `form_${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // 템플릿 코드 생성 유틸리티 함수
  const generateTemplateCode = (title) => {
    // 영문, 숫자, 언더스코어만 남기고 공백은 언더스코어로 변환
    const base = title
      .toLowerCase()
      .replace(/[^a-z0-9_\s]/g, "")
      .replace(/\s+/g, "_");

    // 고유성을 위해 타임스탬프 추가 (선택적)
    // const timestamp = Date.now().toString(36).substring(4);
    // return `${base}_${timestamp}`;

    return base.substring(0, 50); // 50자 제한 (필드 길이)
  };

  // 로딩 표시
  if (isLoading) {
    return <div className="loading">{t("loading")}</div>;
  }

  return (
    <div className="form-builder-container">
      {/* 상단 헤더 */}
      <div className="form-builder-header">
        <div className="form-builder-title">
          {isEditMode ? t("editForm") : t("newForm")}:{" "}
          <select
            value={selectedVersion?.id || ""}
            onChange={(e) => handleVersionChange(Number(e.target.value))}
            className="version-select"
            disabled={isLoadingVersions}
          >
            <option value="">{t("createNewForm")}</option>
            {formVersions.map((version) => (
              <option key={version.id} value={version.id}>
                v{version.version_number} - {version.form_title}
                {version.is_active ? ` (${t("current")})` : ""}
              </option>
            ))}
          </select>
        </div>

        {/* 언어 선택기 추가 */}
        <div className="language-selector">
          <select
            value={currentLanguage}
            onChange={(e) => handleLanguageChange(e.target.value)}
            className="language-select"
          >
            <option value="ko">한국어</option>
            <option value="en">English</option>
          </select>
        </div>

        <div className="form-builder-actions">
          <button className="btn btn-outline" onClick={() => navigate(-1)}>
            <FiChevronLeft /> {t("back")}
          </button>
          <button className="btn btn-outline" onClick={handleDownloadJSON}>
            <FiDownload /> {t("downloadJSON")}
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSaveForm}
            disabled={isSaving}
          >
            <FiSave /> {isSaving ? t("saving") : t("save")}
          </button>
          {isEditMode && (
            <button className="btn btn-secondary" onClick={handlePublishForm}>
              <FiCheck /> {t("publish")}
            </button>
          )}
        </div>
      </div>

      {/* 메인 콘텐츠 영역 */}
      <div className="form-builder-main">
        {/* 왼쪽 도구 패널 */}
        <div
          className={`tools-panel ${toolsPanelCollapsed ? "collapsed" : ""}`}
          style={toolsPanelCollapsed ? {} : { width: toolsPanelWidth }}
        >
          <div className="tools-panel-header">
            <span>{t("tools")}</span>
            <button
              className="toggle-button"
              onClick={toggleToolsPanel}
              title={toolsPanelCollapsed ? t("openPanel") : t("closePanel")}
            >
              {toolsPanelCollapsed ? <FiChevronRight /> : <FiChevronLeft />}
            </button>
          </div>

          {!toolsPanelCollapsed && (
            <div className="tool-group">
              <h4>{t("fieldElements")}</h4>
              <div className="tool-items">
                {Object.entries(FIELD_TYPES).map(([key, type]) => (
                  <div
                    key={type}
                    className="draggable-item"
                    draggable
                    onDragStart={(e) =>
                      handleDragStart(e, "field", { fieldType: type })
                    }
                  >
                    <span className="tool-item-icon">
                      {type === FIELD_TYPES.TEXT && <FiEdit />}
                      {type === FIELD_TYPES.TEXTAREA && <FiList />}
                      {type === FIELD_TYPES.NUMBER && <FiPlay />}
                      {type === FIELD_TYPES.SELECT && <FiList />}
                      {type === FIELD_TYPES.CHECKBOX && <FiCheck />}
                      {type === FIELD_TYPES.RADIO && <FiCheck />}
                      {type === FIELD_TYPES.DATE && <FiCalendar />}
                      {type === FIELD_TYPES.DATETIME && <FiClock />}
                    </span>
                    <span className="tool-item-label">
                      {getFieldLabel(type)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* 좌 패널과 중앙 사이 리사이저 */}
        {!toolsPanelCollapsed && (
          <div
            className="left-resizer"
            onMouseDown={handleLeftResizeMouseDown}
          ></div>
        )}

        {/* 가운데 폼 캔버스 : NewCase 스타일 기본 틀 */}
        <div className="form-canvas builder-preview">
          <div
            className="ohca-form-container preview-only"
            style={{ padding: 0 }}
          >
            <div className="form-content">
              {formStructure.pages.length === 0 && (
                <div className="no-pages-message">
                  <h3>{t("noPages")}</h3>
                  <p>{t("addPagePrompt")}</p>
                </div>
              )}

              {formStructure.pages.map((page, pIdx) => (
                <div
                  key={page.id}
                  className={`form-page${
                    selectedElement.type === "page" &&
                    selectedElement.id === page.id
                      ? " selected"
                      : ""
                  }`}
                  draggable
                  onDragStart={(e) =>
                    handleDragStart(e, "move-element", {
                      elementType: "page",
                      id: page.id,
                    })
                  }
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, "page", page.id)}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleElementClick("page", page.id);
                  }}
                >
                  <h3>{page.title || `${t("pageTitle")} ${pIdx + 1}`}</h3>

                  {/* 섹션 루프 */}
                  {page.sections.length === 0 && (
                    <p className="text-sm text-secondary">{t("noSections")}</p>
                  )}

                  {page.sections.map((section, sIdx) => (
                    <div
                      key={section.id}
                      className={`form-section${
                        selectedElement.type === "section" &&
                        selectedElement.id === section.id
                          ? " selected"
                          : ""
                      }`}
                      draggable
                      onDragStart={(e) =>
                        handleDragStart(e, "move-element", {
                          elementType: "section",
                          id: section.id,
                        })
                      }
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, "section", section.id)}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleElementClick("section", section.id);
                      }}
                    >
                      <h3>{section.title || `${t("section")} ${sIdx + 1}`}</h3>
                      {/* 행 프리뷰 렌더링 */}
                      {section.rows.length === 0 && (
                        <p className="text-sm text-secondary">{t("noRows")}</p>
                      )}

                      {[...section.rows]
                        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                        .map((row, rIdx) => {
                          // 필드 분할: two-column 인 경우 좌/우
                          const leftFields =
                            row.layout === "two-column"
                              ? row.fields.filter(
                                  (f) => !f.column || f.column === "left"
                                )
                              : row.fields;
                          const rightFields =
                            row.layout === "two-column"
                              ? row.fields.filter((f) => f.column === "right")
                              : [];

                          return (
                            <div
                              key={row.id}
                              className={`form-row-preview layout-${
                                row.layout
                              }${
                                selectedElement.type === "row" &&
                                selectedElement.id === row.id
                                  ? " selected"
                                  : ""
                              }`}
                              draggable
                              onDragStart={(e) =>
                                handleDragStart(e, "move-element", {
                                  elementType: "row",
                                  id: row.id,
                                })
                              }
                              onDragOver={handleDragOver}
                              onDragLeave={handleDragLeave}
                              onDrop={(e) => handleDrop(e, "row", row.id)}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleElementClick("row", row.id);
                              }}
                            >
                              <div className="row-label">
                                {row.layout === "two-column"
                                  ? t("twoColumnRow")
                                  : t("singleRow")}
                              </div>

                              {row.layout === "two-column" ? (
                                <div className="two-col-wrapper">
                                  {/* 좌 컬럼 드롭 영역 */}
                                  <div
                                    className="col-preview left-col"
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={(e) =>
                                      handleDrop(e, "row-left", row.id)
                                    }
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (leftFields.length > 0) {
                                        handleElementClick(
                                          "field",
                                          leftFields[0].id
                                        );
                                      } else {
                                        handleElementClick("row", row.id);
                                      }
                                    }}
                                  >
                                    {leftFields.length === 0 ? (
                                      <span className="placeholder">
                                        {t("dragFieldHere")}
                                      </span>
                                    ) : (
                                      leftFields.map((field) => (
                                        <div
                                          key={field.id}
                                          className={`field-preview${
                                            selectedElement.type === "field" &&
                                            selectedElement.id === field.id
                                              ? " selected"
                                              : ""
                                          }`}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleElementClick(
                                              "field",
                                              field.id
                                            );
                                          }}
                                        >
                                          {renderFieldPreview(field)}
                                        </div>
                                      ))
                                    )}
                                  </div>

                                  {/* 우 컬럼 드롭 영역 */}
                                  <div
                                    className="col-preview right-col"
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={(e) =>
                                      handleDrop(e, "row-right", row.id)
                                    }
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (rightFields.length > 0) {
                                        handleElementClick(
                                          "field",
                                          rightFields[0].id
                                        );
                                      } else {
                                        handleElementClick("row", row.id);
                                      }
                                    }}
                                  >
                                    {rightFields.length === 0 ? (
                                      <span className="placeholder">
                                        {t("dragFieldHere")}
                                      </span>
                                    ) : (
                                      rightFields.map((field) => (
                                        <div
                                          key={field.id}
                                          className={`field-preview${
                                            selectedElement.type === "field" &&
                                            selectedElement.id === field.id
                                              ? " selected"
                                              : ""
                                          }`}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleElementClick(
                                              "field",
                                              field.id
                                            );
                                          }}
                                        >
                                          {renderFieldPreview(field)}
                                        </div>
                                      ))
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <div
                                  className="single-col-preview"
                                  onDragOver={handleDragOver}
                                  onDragLeave={handleDragLeave}
                                  onDrop={(e) => handleDrop(e, "row", row.id)}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (row.fields.length > 0) {
                                      handleElementClick(
                                        "field",
                                        row.fields[0].id
                                      );
                                    } else {
                                      handleElementClick("row", row.id);
                                    }
                                  }}
                                >
                                  {row.fields.length === 0 ? (
                                    <span className="placeholder">
                                      {t("dragFieldHere")}
                                    </span>
                                  ) : (
                                    row.fields.map((field) => (
                                      <div
                                        key={field.id}
                                        className={`field-preview${
                                          selectedElement.type === "field" &&
                                          selectedElement.id === field.id
                                            ? " selected"
                                            : ""
                                        }`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleElementClick("field", field.id);
                                        }}
                                      >
                                        {renderFieldPreview(field)}
                                      </div>
                                    ))
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      {/* 행 추가 버튼들 */}
                      <div
                        className="row-add-buttons"
                        style={{
                          display: "flex",
                          gap: "8px",
                          marginTop: "8px",
                        }}
                      >
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddRow(section.id, "full");
                          }}
                        >
                          <FiColumns style={{ marginRight: 4 }} />{" "}
                          {t("addSingleRow")}
                        </button>
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddRow(section.id, "two-column");
                          }}
                        >
                          <FiGrid style={{ marginRight: 4 }} />{" "}
                          {t("addTwoColumnRow")}
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* 섹션 추가 버튼 */}
                  <button
                    className="btn btn-primary btn-sm add-section-button"
                    onClick={() => handleAddSection(page.id)}
                  >
                    <FiPlus /> {t("addSection")}
                  </button>
                </div>
              ))}

              {/* 페이지 추가 버튼 */}
              <div style={{ textAlign: "center", marginTop: "1rem" }}>
                <button
                  className="btn btn-primary btn-sm add-page-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddPage();
                  }}
                >
                  <FiPlus /> {t("addPage")}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* 오른쪽 상세 편집 패널 */}
        <div
          className={`properties-panel ${
            propertiesPanelCollapsed ? "collapsed" : ""
          }`}
          style={propertiesPanelCollapsed ? {} : { width: panelWidth }}
        >
          {!propertiesPanelCollapsed && (
            <div className="resizer" onMouseDown={handleResizeMouseDown}></div>
          )}
          <div className="properties-panel-header">
            <span>{t("detailedEdit")}</span>
            <button
              className="toggle-button"
              onClick={togglePropertiesPanel}
              title={
                propertiesPanelCollapsed ? t("openPanel") : t("closePanel")
              }
            >
              {propertiesPanelCollapsed ? (
                <FiChevronLeft />
              ) : (
                <FiChevronRight />
              )}
            </button>
          </div>
          <div className="properties-content">{renderFormElement()}</div>
        </div>
      </div>
    </div>
  );
};

export default FormBuilder;
